import { z } from 'zod';
import { PageInfo, pageFields } from './paging.js';

/**
 * The bell (N4) and Admin › Sent mail (PRD §18). The bell lists notices addressed to the person
 * signed in, newest first; Waiting on you stays the list of work (reading a notice completes
 * nothing).
 */

/** Opaque paging token: the last item's time and ID, base64url-encoded by the server. */
export const PageCursor = z.string().min(1).max(512);

export const NotificationListQuery = z
  .object({
    cursor: PageCursor.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
  })
  .strict();
export type NotificationListQuery = z.input<typeof NotificationListQuery>;

/**
 * One notice, in the words its reader gets. SENT-108 (APP-204): never the catalog event, which
 * says why it was sent (`overage_decision`: over budget) to a reader the words keep that from.
 */
export const NotificationItem = z.object({
  id: z.uuid(),
  title: z.string(),
  body: z.string().nullable(),
  /** An app path to open, such as `/approvals/…` or `/my-rewards`. */
  href: z.string(),
  createdAt: z.string(),
  read: z.boolean(),
});
export type NotificationItem = z.infer<typeof NotificationItem>;

export const NotificationList = z.object({
  items: z.array(NotificationItem),
  nextCursor: z.string().nullable(),
  unread: z.number().int().min(0),
});
export type NotificationList = z.infer<typeof NotificationList>;

export const NotificationCount = z.object({ unread: z.number().int().min(0) });
export type NotificationCount = z.infer<typeof NotificationCount>;

export const MailStatus = z.enum(['queued', 'sent', 'captured', 'skipped', 'failed']);
export type MailStatus = z.infer<typeof MailStatus>;

export const SentMailQuery = z
  .object({
    status: MailStatus.optional(),
    ...pageFields,
  })
  .strict();
export type SentMailQuery = z.input<typeof SentMailQuery>;

export const SentMailSummary = z.object({
  id: z.uuid(),
  event: z.string(),
  /** The catalog's plain label for the event ("Over-budget approval needed"). */
  eventLabel: z.string(),
  recipient: z.object({
    kind: z.enum(['person', 'group']),
    personId: z.uuid().nullable(),
    name: z.string(),
    address: z.string().nullable(),
  }),
  subject: z.string(),
  status: MailStatus,
  attempts: z.number().int().min(0),
  /** Why the last attempt failed or was skipped, in plain words. */
  lastError: z.string().nullable(),
  /** Where it actually went: outside production, the redirect address. */
  deliveredTo: z.string().nullable(),
  transport: z.enum(['graph', 'capture', 'off']).nullable(),
  award: z.object({ id: z.uuid(), displayId: z.string().nullable() }).nullable(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
  nextAttemptAt: z.string().nullable(),
});
export type SentMailSummary = z.infer<typeof SentMailSummary>;

export const SentMailList = z.object({
  items: z.array(SentMailSummary),
  page: PageInfo,
});
export type SentMailList = z.infer<typeof SentMailList>;

export const SentMailDetail = SentMailSummary.extend({
  /**
   * APP-189: who it was copied to, as the copy list stood when it was written, each on the Cc line
   * or as a blind copy (Sent mail is System Admins' only).
   */
  copiedTo: z.array(
    z.object({ name: z.string(), address: z.string(), mode: z.enum(['cc', 'bcc']) }),
  ),
  textBody: z.string(),
  transportId: z.string().nullable(),
  /** Paths (under `/api`) that stream the content: the sandboxed HTML, and the `.eml` once captured. */
  links: z.object({ html: z.string(), eml: z.string().nullable() }),
  /**
   * The captured `.eml` was deleted by storage retention (30 days, CONTRACT §7). The text and HTML
   * stored with the record are still shown; only the raw message is gone.
   */
  captureExpired: z.boolean(),
});
export type SentMailDetail = z.infer<typeof SentMailDetail>;
